import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "bootstrap/dist/css/bootstrap.min.css";

const NightmareForm = () => {
  const [randomChallenge, setRandomChallenge] = useState({});
  const [captchaSolved, setCaptchaSolved] = useState(false);

  // Generate a new CAPTCHA challenge
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setRandomChallenge({ question: `${num1} + ${num2}`, answer: num1 + num2 });
    setCaptchaSolved(false);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, "❌ At least 2 characters required.")
      .max(50, "❌ Max 50 characters allowed.")
      .required("⚠️ Required field."),
    lastName: Yup.string()
      .min(2, "❌ At least 2 characters required.")
      .max(50, "❌ Max 50 characters allowed.")
      .required("⚠️ Required field."),
    email: Yup.string()
      .email("❌ Invalid email format.")
      .max(100, "❌ Max 100 characters allowed.")
      .required("⚠️ Required field."),
    password: Yup.string()
      .min(8, "❌ At least 8 characters required.")
      .max(30, "❌ Max 30 characters allowed.")
      .matches(/[A-Z]/, "❌ Must contain an uppercase letter.")
      .matches(/[0-9]/, "❌ Must contain a number.")
      .matches(/[@$!%*?&#]/, "❌ Must contain a special character.")
      .required("⚠️ Required field."),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "❌ Passwords must match.")
      .required("⚠️ Required field."),
    phone: Yup.string()
      .matches(/^\d{10}$/, "❌ Must be exactly 10 digits.")
      .required("⚠️ Required field."),
    address: Yup.string()
      .min(10, "❌ At least 10 characters required.")
      .max(200, "❌ Max 200 characters allowed.")
      .required("⚠️ Required field."),
    rangeSlider: Yup.number()
      .min(10, "❌ Minimum value is 10.")
      .max(90, "❌ Maximum value is 90.")
      .required("⚠️ Required field."),
    fileUpload: Yup.mixed()
      .test("fileSize", "❌ File too large (max 2MB).", (value) => !value || value.size <= 2 * 1024 * 1024)
      .test("fileType", "❌ Only JPG, PNG, PDF allowed.", (value) =>
        !value || ["image/jpeg", "image/png", "application/pdf"].includes(value.type)
      ),
  });

  return (
    <div className="container mt-5">
      <h2 className="text-center mb-4">😈 FORM 😈</h2>
      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          confirmPassword: "",
          phone: "",
          address: "",
          fileUpload: null,
          rangeSlider: 50,
          challenge: "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          if (!captchaSolved) {
            alert("🚨 Solve the CAPTCHA first!");
            setSubmitting(false);
            return;
          }
          console.log("✅ Submitted:", values);
          alert("🎉 You survived the Nightmare Form!");
          setSubmitting(false);
        }}
      >
        {({ setFieldValue, isSubmitting }) => (
          <Form>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">First Name *</label>
                <Field name="firstName" className="form-control" placeholder="John" />
                <ErrorMessage name="firstName" component="div" className="text-danger small" />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Last Name *</label>
                <Field name="lastName" className="form-control" placeholder="Doe" />
                <ErrorMessage name="lastName" component="div" className="text-danger small" />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Email *</label>
              <Field type="email" name="email" className="form-control" placeholder="example@email.com" />
              <ErrorMessage name="email" component="div" className="text-danger small" />
            </div>

            <div className="mb-3">
              <label className="form-label">Password *</label>
              <Field type="password" name="password" className="form-control" placeholder="••••••••" />
              <ErrorMessage name="password" component="div" className="text-danger small" />
            </div>

            <div className="mb-3">
              <label className="form-label">Confirm Password *</label>
              <Field type="password" name="confirmPassword" className="form-control" placeholder="••••••••" />
              <ErrorMessage name="confirmPassword" component="div" className="text-danger small" />
            </div>

            <div className="mb-3">
              <label className="form-label">Phone Number *</label>
              <Field name="phone" className="form-control" placeholder="1234567890" />
              <ErrorMessage name="phone" component="div" className="text-danger small" />
            </div>

            <div className="mb-3">
              <label className="form-label">Address *</label>
              <Field as="textarea" name="address" className="form-control" placeholder="Enter your full address" />
              <ErrorMessage name="address" component="div" className="text-danger small" />
            </div>

            <div className="mb-3">
              <label className="form-label">Upload File (Max 2MB, JPG/PNG/PDF)</label>
              <input
                type="file"
                className="form-control"
                onChange={(e) => setFieldValue("fileUpload", e.target.files[0])}
              />
              <ErrorMessage name="fileUpload" component="div" className="text-danger small" />
            </div>

            <div className="mb-3">
              <label className="form-label">Select Range (10-90)</label>
              <Field type="range" name="rangeSlider" min="10" max="90" className="form-range" />
              <ErrorMessage name="rangeSlider" component="div" className="text-danger small" />
            </div>

            <div className="mb-3">
              <label className="form-label">CAPTCHA: {randomChallenge.question}</label>
              <input
                type="text"
                className="form-control"
                onChange={(e) => setCaptchaSolved(Number(e.target.value) === randomChallenge.answer)}
              />
              {!captchaSolved && <div className="text-danger small">❌ Solve CAPTCHA before submitting!</div>}
            </div>

            <button type="button" className="btn btn-warning me-2" onClick={() => alert("Nice try, bot!")}>
              🚫 Fake Submit
            </button>
            <button type="submit" className="btn btn-success" disabled={!captchaSolved || isSubmitting}>
              ✅ Real Submit
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default NightmareForm;
